import { css, keyframes } from "@emotion/core"
import styled from "@emotion/styled"

export const lightTheme =  {
  color: {
    accentLight: "#FDFFFF",
    accentDark: "rgb(255,255,255)",
    accentInverse: "#1C1D1F",
    tabHover: "rgba(64, 157, 219, 0.03)",
    tabSelected: "rgba(64, 157, 219, 0.07)",
    blue: "#3499ff",
    red: "#FF0000",
    action: '#2D275B'
  },
  button: {
    color: '#222222',
    text: '#ffffff'
  },
  size: {
    inner: "1260px",
  },
  text: {
    base: "#222222",
    inverse: "#ffffff",
    lighter: "rgba(255,255,255,.7)",
    light: "rgba(255,255,255,.4)",
    lightest: "rgba(255,255,255,.1)",
  },
  gradient: {
    card: 'linear-gradient(#F7FFFF, #F7FFFF 200px)',
    border: '#ffffff'
  },
  shadow: {
    pane: "0px 3px 5px -1px rgba(0,0,0,0.1), 0px 6px 10px 0px rgba(0,0,0,0.04), 0px 1px 18px 0px rgba(0,0,0,0.02)",
    modal: "0px 3px 5px -1px rgba(0,0,0,0.1), 0px 5px 8px 0px rgba(0,0,0,0.04), 0px 1px 14px 0px rgba(0,0,0,0.02)",
    card: "0px 3px 3px -2px rgba(0,0,0,0.12), 0px 3px 4px 0px rgba(0,0,0,0.06), 0px 1px 8px 0px rgba(0,0,0,0.04)"
  }
}

export const darkTheme =  {
  color: {
    accentLight: "#24282D",
    accentDark: "#15171B",
    tabHover: "rgba(64, 157, 219, 0.03)",
    tabSelected: "rgba(64, 157, 219, 0.07)",
    blue: "#3499ff",
    red: "#FF0000",
    action: '#ffffff'
  },
  button: {
    color: '#ffffff',
    text: '#222222'
  },
  size: {
    inner: "1260px",
  },
  text: {
    base: "#ffffff",
    lighter: "rgba(255,255,255,.7)",
    light: "rgba(255,255,255,.4)",
    lightest: "rgba(255,255,255,.1)",
  },
  gradient: {
    card: 'linear-gradient(#32383D, #24282D 200px)',
    border: '#32383D'
  },
  shadow: {
    pane: "0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)",
    modal: "0px 3px 5px -1px rgba(0,0,0,0.2), 0px 5px 8px 0px rgba(0,0,0,0.14), 0px 1px 14px 0px rgba(0,0,0,0.12)",
    card: "0px 3px 3px -2px rgba(0,0,0,0.25), 0px 3px 4px 0px rgba(0,0,0,0.19), 0px 1px 8px 0px rgba(0,0,0,0.17)"
  }
}

export const dimensions = {
  inbox: {
    left: {
      flex: "350px",
      max: "430px",
    },
    center: {
      flex: "520px",
      min: "400px",
    },
    right: {
      flex: "275px",
      max: "330px",
    },
    banner: {
      height: "",
      background: "#026dd6"
    }
  },
  settings: {
    left: {
      flex: "350px",
      max: "250px",
    },
    center: {
      flex: "520px",
      min: "400px",
    },
  },
}

/*
 * NOTE: use a six-character hex code for all colors to allow alpha channel
 * adjustment without adding extra vars and/or a color manipulation lib.
 *
 * Example:
 *    // use the brand color at 25% opacity
 *    border-color: ${colors.brand}40;
 */
export const colors = {
  brandDarker: "#055701",
  brandDark: "#444",
  brand: "#15ae74",
  theme: "#ff000",
  gray100: "#f8f9fa",
  gray300: "#dee2e6",
  gray400: "#ced4da",
  gray500: "#adb5bd",
  gray600: "#868e96",
  gray700: "#495057",
  gray900: "#212529",
  themeAccent: "#ff0000",
  brandBright: "#ececec",
  brandLight: "#f8f8f8",
  brandLighter: "#fbfafc",
  lightest: "#ffffff",
  darkest: "#4d4058",
  text: "#333333",
  textMild: "#555555",
  textLight: "#7e718a",
  textLighter: "#aaaaaa",
  lilac: `#222`,
  accent: `#055701`,
  error: `#ec1818`,
  lemon: `#ffdf37`,
  white: "#fff",
}

export const spacing = {
  "3xs": 2,
  "2xs": 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  "2xl": 40,
  "3xl": 48,
}

export const breakpoints = {
  mobile: 400,
  phablet: 550,
  tablet: 750,
  desktop: 1000,
  hd: 1300,
}

export const radius = {
  default: 4,
  large: 4,
}

export const defaultFontStack = [
  "-apple-system",
  "BlinkMacSystemFont",
  "Segoe UI",
  "Roboto",
  "Oxygen",
  "Ubuntu",
  "Cantarell",
  "Open Sans",
  "Helvetica Neue",
  "sans-serif",
].join()

const monospaceFontStack = [
  `Space Mono`,
  `SFMono-Regular`,
  `Menlo`,
  `Monaco`,
  `Consolas`,
  `Liberation Mono`,
  `Courier New`,
  `monospace`,
].join()

export const fonts = {
  body: defaultFontStack,
  heading: `Futura PT, ${defaultFontStack}`,
  monospace: monospaceFontStack,
}

const simpleEntry = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
`

const deadSimpleEntry = keyframes`
  from {
    opacity: .25;
  }
`

export const animations = {
  simpleEntry: `${simpleEntry} .75s ease forwards`,
  deadSimpleEntry: `${deadSimpleEntry} .5s ease forwards`,
}
